import { Stack } from '@mui/material';
import { Button } from '../../../../primitives/button';
import { Typography } from '../../../../primitives/typography/typography';
import { TEXT_ACTION } from './text-action';
import { TwoStepCodeForm } from './two-step-forms';
import {
  TWO_STEP_SEND_LIMIT,
  type TwoStepActions,
  type TwoStepFeedback,
  type TwoStepUsing,
  type TwoStepVerifyStep,
} from './two-step.types';

const WAY_LABELS: Record<TwoStepUsing, string> = {
  authenticator: 'Use My Authenticator App',
  sms: 'Text Me a Code',
  'support-code': 'I Have a Code From iDU Support',
};

const verifyBody = ({ using, methods }: TwoStepVerifyStep) => {
  if (using === 'authenticator') return 'Enter the 6-digit code from your authenticator app.';
  if (using === 'support-code') return 'Enter the one-time code iDU support gave you.';
  const hint = methods.find((method) => method.kind === 'sms')?.hint;
  return `Enter the code we texted to ${hint ?? 'your phone'}.`;
};

type TwoStepVerifyProps = TwoStepFeedback &
  Pick<TwoStepActions, 'submitCode' | 'switchMethod' | 'resendCode'> & {
    step: TwoStepVerifyStep;
  };

export const TwoStepVerify = ({
  step,
  busy,
  error,
  submitCode,
  switchMethod,
  resendCode,
}: TwoStepVerifyProps) => {
  const isSupportCode = step.using === 'support-code';
  const ways: TwoStepUsing[] = step.methods.map((method) => method.kind);
  if (step.offersSupportCode) ways.push('support-code');
  const otherWays = ways.filter((using) => using !== step.using);

  return (
    <>
      <Typography.Body sx={{ pb: 1 }}>{verifyBody(step)}</Typography.Body>
      <TwoStepCodeForm
        key={step.using}
        submitLabel="Verify"
        isSupportCode={isSupportCode}
        rememberDays={isSupportCode ? undefined : step.rememberDays}
        busy={busy}
        error={error}
        onSubmit={submitCode}
      />
      {step.using === 'sms' && (
        <Button
          variant="text"
          disabled={busy || error?.code === TWO_STEP_SEND_LIMIT}
          onClick={resendCode}
        >
          Send the Code Again
        </Button>
      )}
      {otherWays.length > 0 && (
        <Stack sx={{ gap: 1, '& > .MuiButton-text': TEXT_ACTION }}>
          <Typography.Subtitle>Try another way</Typography.Subtitle>
          {otherWays.map((using) => (
            <Button key={using} variant="text" disabled={busy} onClick={() => switchMethod(using)}>
              {WAY_LABELS[using]}
            </Button>
          ))}
        </Stack>
      )}
      <Typography.Caption>Can&apos;t use any of these? Contact iDU support.</Typography.Caption>
    </>
  );
};
